<template>
  <div>
    <h2 class="page-title">
      <el-icon><VideoCamera /></el-icon>
      设备视频
    </h2>
    <div class="card">
      <div class="table-operations">
        <div class="query-controls">
          <el-input
            placeholder="输入设备ID搜索"
            style="width: 250px"
            v-model="searchForm.deviceId"
            clearable
            @clear="handleSearch"
            @change="handleSearch"
            @keyup.enter="handleSearch"
            aria-label="设备ID搜索"
          >
            <template #append>
              <el-button :icon="Search" @click="handleSearch"></el-button>
            </template>
          </el-input>
          <el-config-provider :locale="zhCn">
            <div class="date-controls">
              <el-select
                v-model="dateMode"
                class="date-mode"
                aria-label="时间查询方式"
                @change="handleDateModeChange"
              >
                <el-option label="按日" value="day" />
                <el-option label="按月" value="month" />
                <el-option label="按年" value="year" />
                <el-option label="日期范围" value="range" />
              </el-select>
              <el-date-picker
                :key="dateMode"
                v-model="selectedDate"
                :type="datePickerType"
                :format="dateDisplayFormat"
                value-format="YYYY-MM-DD"
                :placeholder="datePlaceholder"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                range-separator="至"
                :disabled-date="isDateDisabled"
                :default-value="calendarDefaultDate"
                :disabled="calendarLoading"
                :editable="false"
                :class="[
                  'video-date-picker',
                  { 'is-range': dateMode === 'range' },
                ]"
                aria-label="视频时间查询"
                clearable
                @change="handleSearch"
              />
            </div>
          </el-config-provider>
          <span class="calendar-hint">{{
            calendarLoading ? "正在读取视频日期…" : "灰色日期暂无视频"
          }}</span>
        </div>
        <el-button :icon="Refresh" @click="handleRefresh">重置</el-button>
      </div>

      <el-table
        :data="videoList"
        stripe
        style="width: 100%"
        v-loading="loading"
      >
        <template #empty>
          <el-empty description="暂无符合条件的视频记录" :image-size="80" />
        </template>
        <el-table-column label="机器名" width="150" align="center">
          <template #default="{ row }">{{ recordDeviceName(row) }}</template>
        </el-table-column>
        <el-table-column prop="videoUrl" label="视频" min-width="300">
          <template #default="scope">
            <div v-if="scope.row.videoUrl" class="video-cell">
              <el-link type="primary" @click="handlePlayVideo(scope.row)">
                <el-icon><VideoPlay /></el-icon>
                播放视频
              </el-link>
              <el-tooltip :content="scope.row.videoUrl" placement="top">
                <span class="video-url">{{ scope.row.videoUrl }}</span>
              </el-tooltip>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdTime" label="上传时间" width="180">
          <template #default="scope">
            {{ formatDate(scope.row.createdTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right" align="center">
          <template #default="scope">
            <div style="display: flex; justify-content: center; gap: 8px">
              <el-button
                size="small"
                :icon="View"
                @click="handleViewDetail(scope.row)"
              >
                详情
              </el-button>
              <el-button
                v-if="isAdminOrAbove"
                size="small"
                type="danger"
                :icon="Delete"
                @click="handleDelete(scope.row)"
              >
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        ></el-pagination>
      </div>
    </div>

    <!-- 详情对话框 -->
    <el-dialog v-model="detailDialogVisible" title="视频详情" width="700px">
      <el-descriptions :column="2" border v-if="currentVideo">
        <el-descriptions-item label="机器名">
          {{ recordDeviceName(currentVideo) }}
        </el-descriptions-item>
        <el-descriptions-item label="上传时间">
          {{ formatDate(currentVideo.createdTime) }}
        </el-descriptions-item>
        <el-descriptions-item label="视频地址" :span="2">
          <el-link
            v-if="currentVideo.videoUrl"
            type="primary"
            :href="currentVideo.videoUrl"
            target="_blank"
          >
            {{ currentVideo.videoUrl }}
          </el-link>
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="视频预览" :span="2">
          <video
            v-if="currentVideo.videoUrl"
            :src="currentVideo.videoUrl"
            controls
            style="width: 100%; max-height: 400px"
          ></video>
          <span v-else>-</span>
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 视频播放对话框 -->
    <el-dialog
      v-model="playDialogVisible"
      title="视频播放"
      width="800px"
      @close="handleCloseVideo"
    >
      <div class="video-container">
        <video
          v-if="currentVideoUrl"
          ref="videoPlayer"
          :src="currentVideoUrl"
          controls
          style="width: 100%; max-height: 500px"
        ></video>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { ElMessage, ElMessageBox } from "element-plus";
import zhCn from "element-plus/es/locale/lang/zh-cn";
import { recordDeviceName } from "@/utils/deviceNames";
import {
  buildDetectionDateRange,
  formatLocalDate,
} from "@/utils/detectionDates";
import {
  VideoCamera,
  Search,
  Refresh,
  View,
  Delete,
  VideoPlay,
} from "@element-plus/icons-vue";
import {
  pageDeviceVideoByLogin,
  pageAllDeviceVideo,
  getDeviceVideoById,
  removeDeviceVideo,
  getDeviceVideoAvailableDates,
} from "@/api/deviceVideo";

export default {
  name: "DeviceVideoList",
  setup() {
    const store = useStore();

    const loading = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const videoList = ref([]);
    const detailDialogVisible = ref(false);
    const playDialogVisible = ref(false);
    const currentVideo = ref(null);
    const currentVideoUrl = ref("");
    const videoPlayer = ref(null);

    const searchForm = reactive({
      deviceId: "",
    });
    const dateMode = ref("day");
    const selectedDate = ref(null);
    const availableDates = ref([]);
    const calendarLoading = ref(false);
    const availableDateSet = computed(() => new Set(availableDates.value));
    const isDateDisabled = (date) =>
      calendarLoading.value ||
      !availableDateSet.value.has(formatLocalDate(date));
    const datePickerType = computed(
      () =>
        ({ day: "date", month: "month", year: "year", range: "daterange" }[
          dateMode.value
        ])
    );
    const dateDisplayFormat = computed(
      () => ({ month: "YYYY-MM", year: "YYYY" }[dateMode.value] || "YYYY-MM-DD")
    );
    const datePlaceholder = computed(
      () =>
        ({ day: "选择视频日期", month: "选择视频月份", year: "选择视频年份" }[
          dateMode.value
        ])
    );
    const calendarDefaultDate = computed(() => {
      const latest = availableDates.value[availableDates.value.length - 1];
      if (!latest) return new Date();
      const [year, month, day] = latest.split("-").map(Number);
      return new Date(year, month - 1, day);
    });
    let listRequestId = 0;
    let calendarRequestId = 0;

    // 判断是否是管理员及以上权限
    const isAdminOrAbove = computed(() => store.getters.isAdminOrAbove);

    // 格式化日期
    const formatDate = (dateStr) => {
      if (!dateStr) return "-";
      const normalizedDateStr =
        typeof dateStr === "string" ? dateStr.replace(" ", "T") : dateStr;
      const date = new Date(normalizedDateStr);
      if (Number.isNaN(date.getTime())) {
        return typeof dateStr === "string" ? dateStr.replace("T", " ") : "-";
      }
      return date.toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    };

    // 获取视频列表
    const fetchVideoList = async () => {
      const requestId = ++listRequestId;
      loading.value = true;
      try {
        const params = {
          pageNum: currentPage.value,
          pageSize: pageSize.value,
          deviceId: searchForm.deviceId.trim() || undefined,
          ...buildDetectionDateRange(dateMode.value, selectedDate.value),
        };

        let res;
        // 管理员及以上权限调用 pageAll 接口，否则调用 page 接口
        if (isAdminOrAbove.value) {
          res = await pageAllDeviceVideo(params);
        } else {
          res = await pageDeviceVideoByLogin(params);
        }

        if (requestId !== listRequestId) return;
        if (res.code === 0 && res.data) {
          videoList.value = res.data.records || [];
          total.value = parseInt(res.data.totalRow) || 0;
        } else {
          videoList.value = [];
          total.value = 0;
          ElMessage.error(res.message || "获取视频列表失败");
        }
      } catch (error) {
        if (requestId !== listRequestId) return;
        videoList.value = [];
        total.value = 0;
        console.error("获取视频列表异常:", error);
        ElMessage.error("获取视频列表失败");
      } finally {
        if (requestId === listRequestId) loading.value = false;
      }
    };

    const fetchAvailableDates = async () => {
      const requestId = ++calendarRequestId;
      calendarLoading.value = true;
      try {
        const res = await getDeviceVideoAvailableDates({
          deviceId: searchForm.deviceId.trim() || undefined,
        });
        if (requestId !== calendarRequestId) return;
        if (res.code !== 0) throw new Error(res.message || "获取视频日期失败");
        availableDates.value = Array.isArray(res.data)
          ? [...res.data].sort()
          : [];
      } catch (error) {
        if (requestId !== calendarRequestId) return;
        availableDates.value = [];
        ElMessage.error(error.message || "获取视频日期失败");
      } finally {
        if (requestId === calendarRequestId) calendarLoading.value = false;
      }
    };

    // 搜索
    const handleSearch = () => {
      currentPage.value = 1;
      fetchVideoList();
      fetchAvailableDates();
    };

    const handleDateModeChange = () => {
      selectedDate.value = null;
      handleSearch();
    };

    // 刷新
    const handleRefresh = () => {
      searchForm.deviceId = "";
      selectedDate.value = null;
      handleSearch();
    };

    // 查看详情
    const handleViewDetail = async (row) => {
      try {
        const res = await getDeviceVideoById(row.id);
        if (res.code === 0) {
          currentVideo.value = res.data;
          detailDialogVisible.value = true;
        } else {
          ElMessage.error(res.message || "获取详情失败");
        }
      } catch (error) {
        console.error("获取详情异常:", error);
        ElMessage.error("获取详情失败");
      }
    };

    // 播放视频
    const handlePlayVideo = (row) => {
      currentVideoUrl.value = row.videoUrl;
      playDialogVisible.value = true;
    };

    // 关闭视频
    const handleCloseVideo = () => {
      currentVideoUrl.value = "";
      if (videoPlayer.value) {
        videoPlayer.value.pause();
      }
    };

    // 删除视频
    const handleDelete = (row) => {
      ElMessageBox.confirm("确定要删除该视频吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          try {
            const res = await removeDeviceVideo(row.id);
            if (res.code === 0) {
              ElMessage.success("删除成功");
              fetchVideoList();
              fetchAvailableDates();
            } else {
              ElMessage.error(res.message || "删除失败");
            }
          } catch (error) {
            console.error("删除异常:", error);
            ElMessage.error("删除失败");
          }
        })
        .catch(() => {});
    };

    const handleSizeChange = (val) => {
      pageSize.value = val;
      currentPage.value = 1;
      fetchVideoList();
    };

    const handleCurrentChange = (val) => {
      currentPage.value = val;
      fetchVideoList();
    };

    // 监听用户信息变化，确保用户信息加载后再获取数据
    const userLoaded = ref(false);

    watch(
      () => store.state.user,
      (newUser) => {
        if (newUser && !userLoaded.value) {
          userLoaded.value = true;
          fetchVideoList();
          fetchAvailableDates();
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      // 如果用户信息已经存在，直接获取数据
      if (store.state.user && !userLoaded.value) {
        userLoaded.value = true;
        fetchVideoList();
        fetchAvailableDates();
      }
    });

    return {
      recordDeviceName,
      VideoCamera,
      Search,
      Refresh,
      View,
      Delete,
      VideoPlay,
      loading,
      videoList,
      currentPage,
      pageSize,
      total,
      searchForm,
      zhCn,
      dateMode,
      selectedDate,
      datePickerType,
      dateDisplayFormat,
      datePlaceholder,
      calendarDefaultDate,
      calendarLoading,
      isDateDisabled,
      handleDateModeChange,
      detailDialogVisible,
      playDialogVisible,
      currentVideo,
      currentVideoUrl,
      videoPlayer,
      isAdminOrAbove,
      formatDate,
      fetchVideoList,
      handleSearch,
      handleRefresh,
      handleViewDetail,
      handlePlayVideo,
      handleCloseVideo,
      handleDelete,
      handleSizeChange,
      handleCurrentChange,
    };
  },
};
</script>

<style scoped>
.page-title {
  font-size: 20px;
  margin-bottom: 20px;
  color: #1b9a7c;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-title .el-icon {
  font-size: 24px;
}

.table-operations {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.query-controls,
.date-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.query-controls {
  flex: 1;
  min-width: 0;
}

.date-mode {
  width: 110px;
}

.date-controls :deep(.video-date-picker) {
  width: 180px;
  flex-grow: 0;
}

.date-controls :deep(.video-date-picker.is-range) {
  width: 280px;
}

.calendar-hint {
  color: #87928f;
  font-size: 12px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.card {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
}

.video-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
  border-radius: 4px;
}

.video-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.video-url {
  color: #909399;
  font-size: 12px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style>
/* 全局样式，修复图片预览层级问题 */
.el-image-viewer__wrapper {
  z-index: 9999 !important;
}
</style>
